import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Inbox, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "../../data/api";

const STATUS = {
  new: { label: "Nuova", cls: "bg-gold/20 text-gold-dark border-gold/40" },
  contacted: { label: "Contattato", cls: "bg-blue-500/15 text-blue-700 border-blue-500/30" },
  closed: { label: "Chiusa", cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
};

export default function QuotesPanel({ logout }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await authFetch("/admin/quotes");
      setQuotes(data);
    } catch (err) {
      if (err.status === 401) logout();
      else toast.error("Errore nel caricamento delle richieste");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await authFetch(`/admin/quotes/${id}`, { method: "PATCH", body: { status } });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Impossibile aggiornare lo stato");
    }
  };

  const filtered = quotes.filter((q) =>
    [q.name, q.phone, q.email, q.service, q.message].join(" ").toLowerCase().includes(query.toLowerCase())
  );
  const newCount = quotes.filter((q) => q.status === "new").length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8 pb-6 border-b border-navy/10">
        <div>
          <h2 className="font-serif font-light text-3xl text-navy">Richieste di preventivo</h2>
          <p className="text-navy/60 mt-1 text-sm">
            {quotes.length} totali · <span className="text-gold-dark font-medium">{newCount} nuove</span>
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca…"
              className="pl-7 pr-4 py-2 bg-transparent border-b border-navy/20 text-navy placeholder:text-navy/35 focus:outline-none focus:border-gold w-full sm:w-64"
            />
          </div>
          <button onClick={load} aria-label="Aggiorna" className="p-2 text-navy/50 hover:text-gold transition-colors"><RefreshCw size={16} /></button>
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center text-navy/40">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="py-24 text-center text-navy/40 flex flex-col items-center gap-4">
          <Inbox size={48} className="text-navy/20" />
          Nessuna richiesta {query ? "trovata" : "ancora"}.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((q, i) => (
            <motion.article
              key={q.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.02 }}
              className="bg-white border border-gold/20 p-6 grid md:grid-cols-12 gap-5 items-start"
            >
              <div className="md:col-span-3">
                <p className="font-serif text-xl text-navy">{q.name}</p>
                <p className="text-navy/50 text-xs mt-1">{new Date(q.created_at).toLocaleString("it-IT")}</p>
                {q.service && <span className="inline-block mt-3 text-[0.62rem] uppercase tracking-[0.18em] text-gold-dark border border-gold/30 px-2 py-1">{q.service}</span>}
              </div>
              <div className="md:col-span-3 space-y-2 text-sm">
                <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-navy hover:text-gold transition-colors"><Phone size={14} className="text-gold" /> {q.phone}</a>
                {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-navy hover:text-gold transition-colors"><Mail size={14} className="text-gold" /> {q.email}</a>}
              </div>
              <div className="md:col-span-4 text-sm text-navy/70 leading-relaxed">
                {q.message || <span className="text-navy/30 italic">Nessun messaggio</span>}
              </div>
              <div className="md:col-span-2 flex md:justify-end">
                <select
                  value={q.status}
                  onChange={(e) => setStatus(q.id, e.target.value)}
                  className={`text-[0.65rem] uppercase tracking-[0.15em] px-3 py-2 border outline-none cursor-pointer ${STATUS[q.status]?.cls || STATUS.new.cls}`}
                >
                  <option value="new">Nuova</option>
                  <option value="contacted">Contattato</option>
                  <option value="closed">Chiusa</option>
                </select>
              </div>
            </motion.article>
          ))}
        </div>
      )}
    </div>
  );
}
